import { useEffect, useState } from "react";
import axios from "axios";
import { Loader2, ThumbsUp, ThumbsDown, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Friendly labels mirrored from FaqPage's FAQ_ITEMS so the admin sees the
// real question text instead of opaque IDs. If a new question is added to
// the FAQ but not here, the raw ID falls through (intentional — flags drift).
const QUESTION_LABELS = {
  "what-is-bilateral-stimulation": "What is bilateral stimulation?",
  "how-is-bilateral-sound-different":
    "How is bilateral sound different from regular meditation audio?",
  "insomnia-and-sleep-anxiety":
    "Can Bilateral Calm help with insomnia and sleep anxiety?",
  "vs-emdr-therapy": "Is Bilateral Calm the same as EMDR therapy?",
  "supported-conditions": "What conditions are the practices designed for?",
};

const ALL_QUESTIONS = Object.keys(QUESTION_LABELS);

// Healthy = visitors find it useful; struggling = lots of "no" votes.
const rateBadgeStyle = (rate) => {
  if (rate == null) return "bg-slate-100 text-[#718096]";
  if (rate >= 0.8) return "bg-[#E7F4EA] text-[#2D7E47]";
  if (rate >= 0.5) return "bg-[#FEF3E7] text-[#B07B3A]";
  return "bg-[#FEF2F0] text-[#A65C58]";
};

export const FaqInsightsPanel = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/faq/feedback`, { withCredentials: true });
        if (cancelled) return;
        // Backfill questions with zero votes so admins see every FAQ even
        // before anyone has voted — much less confusing than an empty table.
        const byId = Object.fromEntries(
          (res.data || []).map((r) => [r.question_id, r])
        );
        const filled = ALL_QUESTIONS.map(
          (qid) => byId[qid] || { question_id: qid, yes: 0, no: 0, total: 0, helpful_rate: null }
        );
        setRows(filled);
      } catch (e) {
        if (!cancelled) setError("Couldn't load FAQ feedback.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 flex items-center justify-center" data-testid="faq-insights-loading">
        <Loader2 className="w-5 h-5 text-[#3090A8] animate-spin mr-2" />
        <span className="text-[#718096]">Loading feedback…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100 text-[#A65C58]" data-testid="faq-insights-error">
        {error}
      </div>
    );
  }

  const totalsYes = rows.reduce((sum, r) => sum + r.yes, 0);
  const totalsNo = rows.reduce((sum, r) => sum + r.no, 0);
  const totalsAll = totalsYes + totalsNo;
  const overallRate = totalsAll ? Math.round((totalsYes / totalsAll) * 1000) / 10 : null;

  return (
    <div className="bg-white rounded-2xl p-6 shadow-[0_4px_20px_rgba(0,0,0,0.03)] border border-slate-100" data-testid="faq-insights-panel">
      <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-xl bg-[#E7F0F4] flex items-center justify-center">
            <MessageCircle className="w-4 h-4 text-[#3090A8]" />
          </div>
          <div>
            <h2 className="font-outfit font-medium text-lg text-[#1B2349]">FAQ Insights</h2>
            <p className="text-[#718096] text-sm">Visitor votes on each FAQ answer. Lower scores = candidates for a copy edit.</p>
          </div>
        </div>
        {overallRate != null && (
          <div className="text-right" data-testid="faq-insights-overall">
            <div className="text-[#718096] text-xs uppercase tracking-wide">Overall</div>
            <div className="font-outfit font-semibold text-2xl text-[#1B2349]">
              {overallRate}<span className="text-base text-[#718096]">% helpful</span>
            </div>
            <div className="text-xs text-[#718096]">{totalsAll} vote{totalsAll === 1 ? "" : "s"}</div>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-slate-100">
              <th className="text-left py-3 px-3 text-[#718096] font-medium">Question</th>
              <th className="text-left py-3 px-3 text-[#718096] font-medium w-24">Yes</th>
              <th className="text-left py-3 px-3 text-[#718096] font-medium w-24">No</th>
              <th className="text-left py-3 px-3 text-[#718096] font-medium w-32">Helpful</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.question_id} className="border-b border-slate-50 last:border-b-0" data-testid={`faq-insights-row-${r.question_id}`}>
                <td className="py-3 px-3 text-[#1B2349]">
                  <div className="font-medium">{QUESTION_LABELS[r.question_id] || r.question_id}</div>
                  <div className="text-[11px] text-[#A0AEC0] mt-0.5">{r.question_id}</div>
                </td>
                <td className="py-3 px-3 text-[#2D7E47] font-medium">
                  <span className="inline-flex items-center gap-1">
                    <ThumbsUp className="w-3.5 h-3.5" />
                    {r.yes}
                  </span>
                </td>
                <td className="py-3 px-3 text-[#A65C58] font-medium">
                  <span className="inline-flex items-center gap-1">
                    <ThumbsDown className="w-3.5 h-3.5" />
                    {r.no}
                  </span>
                </td>
                <td className="py-3 px-3">
                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${rateBadgeStyle(r.helpful_rate)}`}>
                    {r.helpful_rate == null
                      ? "No votes yet"
                      : `${Math.round(r.helpful_rate * 1000) / 10}%`}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default FaqInsightsPanel;
